import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Dialog, DialogContent, DialogTitle, IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import InvButton from '../../../components/InvButton';
import { exchangeRefund } from '../../../api/pharmacyDispense';
import { getStores } from '../../../api/stockLedger';
import { resolvePharmacyScope } from '../../../hooks/usePharmacyRole';
import useInvRole from '../../../hooks/useInvRole';
import { INV_ACTIONS } from '../../../utils/inventoryRoles';
import { formatINR, parseMoney } from '../../../utils';
import { pharmacyRefundCnNo, pharmacyRefundRemaining } from '../../../utils/pharmacyMappers';
import { IPD_COLORS, cardSx } from '../../../theme';
import useDirectDispensePipeline from '../direct/useDirectDispensePipeline';
import DirectDispenseMedicineStep, {
  MEDICINE_LINE_DEFAULT,
  medicineLinesValid,
} from '../direct/DirectDispenseMedicineStep';

// Server and client price the same basket independently; anything beyond a
// paisa-level rounding gap means the payment split no longer adds up.
const PRICE_TOLERANCE = 0.05;

function money4(value) {
  return String(parseMoney(value).toFixed(4));
}

/**
 * Exchange an approved refund against replacement medicines.
 *
 * The replacement basket goes through the normal sale -> bill -> finalize
 * pipeline so the server prices it; the exchange call then settles the refund
 * against that finalized bill and collects only the amount by which it exceeds
 * the refund. Nothing is ever paid back to the customer.
 */
function ExchangeBody({ refund, billId, scope: scopeProp, patientDisplay, onCompleted }) {
  const pipeline = useDirectDispensePipeline();
  const { can } = useInvRole();
  const [lines, setLines] = useState([{ ...MEDICINE_LINE_DEFAULT }]);
  const [storeId, setStoreId] = useState('');
  const [store, setStore] = useState(null);
  const [storeError, setStoreError] = useState(null);
  const [done, setDone] = useState(null);

  const refundId = refund?.id || refund?.refund_id;
  const refundBillId = billId || refund?.bill_id || refund?.pharmacy_bill_id;
  const credit = pharmacyRefundRemaining(refund);
  const cnNo = pharmacyRefundCnNo(refund);

  useEffect(() => {
    getStores({ is_pharmacy_store: true })
      .then((data) => {
        const storeList = Array.isArray(data) ? data : (data?.items || []);
        const first = storeList[0];
        if (!first) {
          setStoreError('No pharmacy store is available for this facility');
          return;
        }
        setStoreId(first.store_id || first.id || '');
        setStore(first);
      })
      .catch((err) => setStoreError(err?.message || 'Could not load pharmacy stores'));
  }, []);

  const remarks = useMemo(() => `Exchange against refund ${cnNo}`, [cnNo]);

  async function prepareBill() {
    if (!storeId) throw new Error('No pharmacy store available');
    if (!medicineLinesValid(lines)) {
      throw new Error('Add at least one medicine with a valid quantity');
    }
    return pipeline.runPrepareBill({
      lines,
      storeId,
      store,
      patientDisplay: patientDisplay || {},
      remarks,
    });
  }

  async function completeExchange(modes) {
    if (!refundId || !refundBillId) throw new Error('Refund is missing its bill reference');

    // A failed exchange leaves a finalized, unpaid replacement bill behind; a
    // retry must reuse it rather than price (and dispense) a second basket.
    const resume = pipeline.state.billId && pipeline.state.billStatus === 'FINALIZED';
    const prepared = resume
      ? {
        billId: pipeline.state.billId,
        amountPayable: pipeline.state.amountPayable,
        scope: pipeline.state.scope,
      }
      : await prepareBill();

    const payable = parseMoney(prepared.amountPayable);
    if (payable + PRICE_TOLERANCE < credit) {
      throw new Error(
        `Replacement items total ${formatINR(payable)}, which is less than the refund of `
        + `${formatINR(credit)}. Cash cannot be returned — discard this exchange and start `
        + `again with items worth at least ${formatINR(credit)}.`,
      );
    }

    const due = Math.max(0, payable - credit);
    let exchangeModes = [];
    if (due > 0.005) {
      const allocated = (modes || []).reduce((sum, m) => sum + parseMoney(m.amount), 0);
      if (!modes?.length || Math.abs(allocated - due) > PRICE_TOLERANCE) {
        throw new Error(
          `The customer must pay ${formatINR(due)} (replacement ${formatINR(payable)} less `
          + `refund ${formatINR(credit)}) but payment totals ${formatINR(allocated)}. `
          + 'Adjust the payment modes and try again.',
        );
      }
      // Land the exact server-priced difference on the first mode, as the
      // direct-dispense payment step does.
      const diff = due - allocated;
      exchangeModes = modes.map((m, i) => ({
        ...m,
        amount: money4(parseMoney(m.amount) + (i === 0 ? diff : 0)),
      }));
    }

    await pipeline.finalizeForExchange(prepared.scope);
    const scope = resolvePharmacyScope(store, scopeProp || prepared.scope || {});
    const result = await exchangeRefund(refundBillId, refundId, {
      replacement_bill_id: prepared.billId,
      // Derived from the pair being exchanged, so a retry after a timeout is
      // recognised by the server as the same request, not a second exchange.
      idempotency_key: `exch-${refundId}-${prepared.billId}`,
      modes: exchangeModes,
    }, scope);

    setDone({ credit, due, result });
    toast.success('Exchange completed — stock return posted to ledger');
  }

  if (done) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CheckCircleIcon sx={{ fontSize: 56, color: IPD_COLORS.success.text, mb: 1 }} />
        <Typography sx={{ fontWeight: 800, fontSize: 20, mb: 0.5 }}>Exchange complete</Typography>
        <Typography sx={{ fontSize: 14, mb: 0.5 }}>
          Refund {cnNo} of {formatINR(done.credit)} applied to the replacement items.
        </Typography>
        <Typography sx={{ fontSize: 14, mb: 2 }}>
          {done.due > 0.005 ? `Customer paid ${formatINR(done.due)}.` : 'No additional payment was due.'}
        </Typography>
        <InvButton onClick={onCompleted}>Done</InvButton>
      </Box>
    );
  }

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Cash refunds are not permitted by this hospital. Select replacement medicines worth{' '}
        <strong>{formatINR(credit)}</strong> or more against refund {cnNo}; the customer pays
        any difference.
      </Alert>
      {storeError && <Alert severity="error" sx={{ mb: 2 }}>{storeError}</Alert>}
      <DirectDispenseMedicineStep
        lines={lines}
        setLines={setLines}
        storeId={storeId}
        store={store}
        isSupervisor={can(INV_ACTIONS.CONTROLLED_OVERRIDE)}
        serverTotals={null}
        pipeline={pipeline}
        patientLabel={patientDisplay?.name || 'Walk-in'}
        doctorName=""
        patientDisplay={patientDisplay}
        saleFinalized={pipeline.state.saleFinalized}
        onPrepareBill={prepareBill}
        onPrepareAndPay={completeExchange}
        onNewDispense={() => {}}
        saleRemarks={remarks}
        requireStoreForSearch={false}
        exchange={{ creditAmount: credit }}
      />
    </>
  );
}

export default function RefundExchangeDialog({
  open,
  refund,
  billId,
  scope,
  patientDisplay,
  onClose,
  onCompleted,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: 18 }}>
          Exchange {refund ? pharmacyRefundCnNo(refund) : ''}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Mounted only while open so every exchange starts from a clean pipeline. */}
        {open && refund && (
          <ExchangeBody
            refund={refund}
            billId={billId}
            scope={scope}
            patientDisplay={patientDisplay}
            onCompleted={onCompleted}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
